import { z } from 'zod';

/**
 * DTO + zod validation schemas — input surface for the service layer.
 * Keep these lean; adapter-specific coercion stays in the kgc-api controller.
 *
 * Frontend-safe (NO NestJS imports) — Vite/Rollup bundles cleanly. S17W4-C.
 */

export const EQUIPMENT_TYPE_CATEGORIES = [
  'SMALL_ENGINE',
  'LAWN_EQUIPMENT',
  'POWER_TOOL',
  'OTHER',
] as const;
export type EquipmentTypeCategory = (typeof EQUIPMENT_TYPE_CATEGORIES)[number];

// M4 CR patch: strict https-only image URL (blocks javascript:/data:/file: vectors).
const httpsImageUrl = z
  .string()
  .url()
  .refine(v => /^https:\/\//.test(v), {
    message: 'Image URL must use https://',
  });

// ─────────────────────────────────────────────────────────────────────────────
// STORY-1306 slice C: webshop-tab seam (4-locale szövegek + slug + 3 visibility-flag)
// ─────────────────────────────────────────────────────────────────────────────

/** Webshop slug — kisbetű/szám/kötőjel, tenant-en belül egyedi (DB @@unique). */
export const equipmentTypeSlug = z
  .string()
  .min(1)
  .max(255)
  .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/, {
    message: 'A slug csak kisbetűt, számot és kötőjelet tartalmazhat (pl. makita-hr2470)',
  });

/** Egy locale webshop-szövege. */
export const webshopLocaleTextSchema = z.object({
  name: z.string().max(255).optional(),
  description: z.string().optional(),
});
/** Per-locale webshop-szövegek (4 nyelv: hu/en/de/uk). */
export const webshopTextsSchema = z.object({
  hu: webshopLocaleTextSchema.optional(),
  en: webshopLocaleTextSchema.optional(),
  de: webshopLocaleTextSchema.optional(),
  uk: webshopLocaleTextSchema.optional(),
});
export type WebshopTexts = z.infer<typeof webshopTextsSchema>;

export const createEquipmentTypeSchema = z.object({
  name: z.string().min(1).max(255),
  category: z.enum(EQUIPMENT_TYPE_CATEGORIES),
  sku: z.string().min(1).max(100),
  manufacturerSku: z.string().max(100).nullable().optional(),
  alternativeSkus: z.array(z.string().max(100)).max(50).default([]),
  brand: z.string().max(100).nullable().optional(),
  model: z.string().max(100).nullable().optional(),
  description: z.string().nullable().optional(),
  specsJson: z.record(z.unknown()).nullable().optional(),
  // STORY-1306 D5 Phase 1: image fields. CR patch M4: https-only + .max(50) gallery cap.
  heroImageUrl: httpsImageUrl.nullable().optional(),
  galleryImages: z.array(httpsImageUrl).max(50).default([]),
  // STORY-1306 slice C: webshop-tab mezők (additive, mind opcionális — non-breaking).
  // A flag-defaultok a DB-n (@default), NEM Zod-on → a CreateDto output-típusa nem
  // kényszeríti a meglévő create-hívókat (EquipmentTypeEditorPage stb.) az új mezőkre.
  slug: equipmentTypeSlug.nullable().optional(),
  webshopTexts: webshopTextsSchema.nullable().optional(),
  visibleOnWebshop: z.boolean().optional(),
  availableForRent: z.boolean().optional(),
  availableForSale: z.boolean().optional(),
  isActive: z.boolean().default(true),
});

export type CreateEquipmentTypeDto = z.infer<typeof createEquipmentTypeSchema>;

export const updateEquipmentTypeSchema = createEquipmentTypeSchema.partial();
export type UpdateEquipmentTypeDto = z.infer<typeof updateEquipmentTypeSchema>;

export const createEquipmentUnitSchema = z.object({
  typeId: z.string().uuid(),
  serialNumber: z.string().min(1).max(100),
  assetTag: z.string().max(100).nullable().optional(),
  status: z
    .enum([
      'AVAILABLE',
      'RESERVED',
      'IN_USE',
      'DAMAGED',
      'UNDER_REPAIR',
      'SOLD',
      'WITHDRAWAL_REQUESTED',
      'RETIRED',
    ])
    .default('AVAILABLE'),
  operatingHours: z.number().nonnegative().nullable().optional(),
  locationId: z.string().uuid().nullable().optional(),
  // M7 CR patch: z.coerce.date() accepts ISO strings from HTTP JSON bodies.
  purchaseDate: z.coerce.date().nullable().optional(),
  purchasePrice: z.number().nonnegative().nullable().optional(),
  warrantyEndAt: z.coerce.date().nullable().optional(),
  notes: z.string().nullable().optional(),
});

export type CreateEquipmentUnitDto = z.infer<typeof createEquipmentUnitSchema>;

export const updateEquipmentUnitSchema = createEquipmentUnitSchema.partial();
export type UpdateEquipmentUnitDto = z.infer<typeof updateEquipmentUnitSchema>;

export const createServicePackageSchema = z
  .object({
    typeId: z.string().uuid(),
    name: z.string().min(1).max(255),
    description: z.string().nullable().optional(),
    intervalHours: z.number().int().positive().nullable().optional(),
    intervalDays: z.number().int().positive().nullable().optional(),
    price: z.number().nonnegative().nullable().optional(),
    isActive: z.boolean().default(true),
  })
  .refine(v => v.intervalHours != null || v.intervalDays != null, {
    message: 'At least one of intervalHours or intervalDays is required',
    path: ['intervalHours'],
  });

export type CreateServicePackageDto = z.infer<typeof createServicePackageSchema>;

// ─────────────────────────────────────────────────────────────────────────────
// equipment/01-unit-editor SEAM (read + Tab-1 PATCH + status-transition)
// Read-seam-first kontraktus a FE unit-editorhoz. A kanonikus EquipmentUnitStatus
// (8-state, types.ts) + EQUIPMENT_TYPE_CATEGORIES + a @kgc/ui komponens-shape-ek
// (Timeline / StatusTransitionModal) illesztve → 0 FE-adapter.
// ─────────────────────────────────────────────────────────────────────────────

/** Unit-status tuple a z.enum-hoz (= EQUIPMENT_UNIT_STATUSES, types.ts). */
const UNIT_STATUS_VALUES = [
  'AVAILABLE',
  'RESERVED',
  'IN_USE',
  'DAMAGED',
  'UNDER_REPAIR',
  'SOLD',
  'WITHDRAWAL_REQUESTED',
  'RETIRED',
] as const;

/**
 * Tab-2 státusz-history bejegyzés — SEAM-NATIVE (render-ready emberi stringek).
 * SZÁNDÉKOSAN NEM a @kgc/ui Timeline `TimelineEvent`-jéhez igazítva: a Timeline a `StatusBadge`-en
 * át renderel, ami csak 4 equipment-statet ismer → a 8-state EquipmentUnitStatus-hoz a FE custom
 * timeline + 8-state badge megy (Botka measured-recon). Ez a richebb shape teljes 8-state kontroll.
 */
export const equipmentUnitStatusHistoryEntrySchema = z.object({
  status: z.enum(UNIT_STATUS_VALUES),
  at: z.coerce.date(),
  /** Emberi időtartam-label, pl. „3 nap" */
  durationLabel: z.string(),
  /** Emberi státusz-label */
  label: z.string(),
  /** Mi váltotta ki (user/rendszer/esemény) */
  trigger: z.string(),
});
export type EquipmentUnitStatusHistoryEntry = z.infer<typeof equipmentUnitStatusHistoryEntrySchema>;

/** Tab-3 kép. */
export const equipmentUnitImageSchema = z.object({
  id: z.string(),
  url: z.string(),
  caption: z.string().nullable(),
});
/** Tab-4 dokumentum. */
export const equipmentUnitDocumentSchema = z.object({
  id: z.string(),
  name: z.string(),
  kind: z.string(),
  sizeBytes: z.number().int().nonnegative(),
  url: z.string(),
  expiresAt: z.coerce.date().nullable(),
});

/**
 * READ-DTO — a 4-tab unit-editor betöltő shape-je (GET /equipment/:id → data).
 * Aggregátum: unit-mezők + type-referencia + media/history. A FE erre pin-eli a proto-parity-t + MSW-mockot.
 * Field-mapping (BE→contract): internalSku=assetTag · hours=operatingHours · purchasedAt=purchaseDate ·
 * note=notes · category/manufacturerSku/altSkus a type-ról aggregálva.
 */
export const equipmentUnitReadSchema = z.object({
  id: z.string().uuid(),
  typeId: z.string().uuid(),
  typeName: z.string(),
  internalSku: z.string(),
  manufacturerSku: z.string().nullable(),
  altSkus: z.array(z.string()),
  serialNumber: z.string(),
  purchasedAt: z.coerce.date().nullable(),
  hours: z.number().nonnegative().nullable(),
  category: z.enum(EQUIPMENT_TYPE_CATEGORIES),
  note: z.string().nullable(),
  status: z.enum(UNIT_STATUS_VALUES),
  statusSince: z.coerce.date(),
  statusHistory: z.array(equipmentUnitStatusHistoryEntrySchema),
  images: z.array(equipmentUnitImageSchema),
  documents: z.array(equipmentUnitDocumentSchema),
});
export type EquipmentUnitRead = z.infer<typeof equipmentUnitReadSchema>;

export const equipmentUnitReadResponseSchema = z.object({ data: equipmentUnitReadSchema });
export type EquipmentUnitReadResponse = z.infer<typeof equipmentUnitReadResponseSchema>;

/** Tab-1 PATCH — szerkeszthető mezők (a status külön transition; typeId/internalSku readonly). */
export const equipmentUnitEditorPatchSchema = z.object({
  manufacturerSku: z.string().nullable().optional(),
  altSkus: z.array(z.string()).optional(),
  serialNumber: z.string().min(1).max(100).optional(),
  purchasedAt: z.coerce.date().nullable().optional(),
  hours: z.number().nonnegative().nullable().optional(),
  note: z.string().nullable().optional(),
});
export type EquipmentUnitEditorPatch = z.infer<typeof equipmentUnitEditorPatchSchema>;

/** Reason-kötelező státuszok (audit-justification a „komoly" átmenetekhez). */
export const UNIT_STATUS_REASON_REQUIRED = [
  'DAMAGED',
  'RETIRED',
  'SOLD',
  'WITHDRAWAL_REQUESTED',
] as const;
/** Fotó-kötelező státuszok. */
export const UNIT_STATUS_PHOTO_REQUIRED = ['DAMAGED'] as const;

/**
 * STATUS-TRANSITION DTO — POST /equipment/:id/status (domain-action).
 * A @kgc/ui `StatusTransitionModal` onConfirm-jához illesztve: `from` a jelenlegi státuszból
 * (server-derived), `photoIds` a PhotoUpload `UploadedPhoto.id`-kból → 0 FE-adapter.
 * A target-listát a hívó a `EQUIPMENT_UNIT_TRANSITIONS` állapotgéppel (canTransition) gate-eli.
 */
export const equipmentUnitStatusTransitionSchema = z
  .object({
    targetStatus: z.enum(UNIT_STATUS_VALUES),
    reason: z.string().optional(),
    photoIds: z.array(z.string()).optional(),
  })
  .superRefine((val, ctx) => {
    if ((UNIT_STATUS_REASON_REQUIRED as readonly string[]).includes(val.targetStatus)) {
      if (!val.reason || val.reason.trim().length < 20) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['reason'],
          message: 'Indoklás kötelező (minimum 20 karakter) ennél a státusznál',
        });
      }
    }
    if ((UNIT_STATUS_PHOTO_REQUIRED as readonly string[]).includes(val.targetStatus)) {
      if (!val.photoIds || val.photoIds.length === 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['photoIds'],
          message: 'Legalább egy fotó kötelező DAMAGED státusznál',
        });
      }
    }
  });
export type EquipmentUnitStatusTransition = z.infer<typeof equipmentUnitStatusTransitionSchema>;

// ─────────────────────────────────────────────────────────────────────────────
// equipment-type/webshop-tab SEAM (read + PATCH) — STORY-1306 slice C
// A FE 4-locale tab-UI erre pin-eli a kontraktust + MSW-mockot. Field-mapping:
// `webshopTexts` a Prisma `webshopTextsJson`-ból parse-olva; a 3 flag + `slug` 1:1.
// ─────────────────────────────────────────────────────────────────────────────

/** READ-shape: GET /equipment-types/:id webshop-fragmentje (a tab betöltő shape). */
export const equipmentTypeWebshopReadSchema = z.object({
  id: z.string().uuid(),
  slug: z.string().nullable(),
  webshopTexts: webshopTextsSchema.nullable(),
  visibleOnWebshop: z.boolean(),
  availableForRent: z.boolean(),
  availableForSale: z.boolean(),
});
export type EquipmentTypeWebshopRead = z.infer<typeof equipmentTypeWebshopReadSchema>;

/**
 * PATCH-shape: a webshop-tab szerkeszthető mezői (mind opcionális).
 * HU-név-guard (Javo-ratify 2026-06-11): ha ugyanebben a payloadban a webshop-láthatóságot
 * BEKAPCSOLJÁK (`visibleOnWebshop === true`) ÉS `webshopTexts` is jön, akkor a HU-név kötelező
 * — nevetlen terméket ne lehessen a webshopra tenni. A teljes merged-state (meglévő HU-név +
 * patch) ellenőrzése a BE-service feladata (a guard a same-payload esetet fogja contract-szinten).
 */
export const equipmentTypeWebshopPatchSchema = z
  .object({
    slug: equipmentTypeSlug.nullable().optional(),
    webshopTexts: webshopTextsSchema.nullable().optional(),
    visibleOnWebshop: z.boolean().optional(),
    availableForRent: z.boolean().optional(),
    availableForSale: z.boolean().optional(),
  })
  .superRefine((val, ctx) => {
    if (val.visibleOnWebshop === true && val.webshopTexts !== undefined) {
      const huName = val.webshopTexts?.hu?.name;
      if (!huName || huName.trim().length === 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['webshopTexts', 'hu', 'name'],
          message: 'A magyar név kötelező, ha a termék a webshopon látható',
        });
      }
    }
  });
export type EquipmentTypeWebshopPatch = z.infer<typeof equipmentTypeWebshopPatchSchema>;

// ─────────────────────────────────────────────────────────────────────────────
// equipment-type/docs-tab SEAM (read-only lista, Document-reuse) — STORY-1306 slice D
// NINCS dedikált tábla: a meglévő `Document` modell + `kind` diszkriminátor +
// entity-link (mint a BE-1 handover-fotó). Endpoint: GET /equipment-types/:id/documents.
// Upload külön slice (read-only listával indul, Botka-egyeztetés 2026-06-11).
// ─────────────────────────────────────────────────────────────────────────────

/** Equipment-type doc-kind konvenció (R10 használati/biztonsági + jótállás/tanúsítvány). */
export const EQUIPMENT_TYPE_DOCUMENT_KINDS = [
  'MANUAL', // használati útmutató
  'SAFETY', // biztonsági figyelmeztetés
  'WARRANTY', // jótállás
  'CERTIFICATE', // tanúsítvány
  'OTHER',
] as const;
export type EquipmentTypeDocumentKind = (typeof EQUIPMENT_TYPE_DOCUMENT_KINDS)[number];

/** Egy doc-bejegyzés (Document-reuse shape, a unit-editor `equipmentUnitDocumentSchema` mintájára). */
export const equipmentTypeDocumentSchema = z.object({
  id: z.string(),
  name: z.string(),
  kind: z.enum(EQUIPMENT_TYPE_DOCUMENT_KINDS),
  sizeBytes: z.number().int().nonnegative(),
  url: z.string(),
  uploadedAt: z.coerce.date(),
});
export type EquipmentTypeDocument = z.infer<typeof equipmentTypeDocumentSchema>;

/** READ-shape: GET /equipment-types/:id/documents → `{ data: [...] }`. */
export const equipmentTypeDocumentsReadSchema = z.object({
  data: z.array(equipmentTypeDocumentSchema),
});
export type EquipmentTypeDocumentsRead = z.infer<typeof equipmentTypeDocumentsReadSchema>;
